import { useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import {
  useLazySearchMeetingsArchiveQuery,
  useAnswerMeetingsArchiveMutation,
  useReindexMeetingsArchiveMutation,
  useListMeetingDepartmentsQuery,
} from '@shared/api';
import { useAgentPermission, useMeetingDeleteAccess } from '@entities/user';
import { fmtMeetingDate, protocolKindLabel } from '@entities/meetings';
import type { ArchiveMatch } from '@entities/meetings';
import { Card } from '@shared/ui/Card';
import { Button } from '@shared/ui/Button';
import { FallbackBanner } from '@shared/ui/FallbackBanner';

const Intro = styled.p`
  margin: 0 0 ${({ theme }) => theme.spacing(5)};
  font-size: 13px;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.text.secondary};
  max-width: 720px;
`;

const Form = styled.form`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing(3)};
  flex-wrap: wrap;
  margin-bottom: ${({ theme }) => theme.spacing(6)};
`;

const QueryInput = styled.input`
  flex: 1 1 320px;
  min-width: 240px;
  font-size: 14px;
  padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(3)}`};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text.primary};

  &:focus-visible {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }
`;

const FilterSelect = styled.select`
  font-size: 13px;
  padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(3)}`};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text.primary};
  cursor: pointer;

  &:focus-visible {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }
`;

const AnswerCard = styled(Card)`
  margin-bottom: ${({ theme }) => theme.spacing(6)};
  border-left: 3px solid ${({ theme }) => theme.colors.primary};
`;

const AnswerLabel = styled.div`
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: ${({ theme }) => theme.spacing(3)};
`;

const AnswerText = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.65;
  white-space: pre-wrap;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const CiteHint = styled.div`
  margin-top: ${({ theme }) => theme.spacing(3)};
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const Section = styled.section`
  & + & {
    margin-top: ${({ theme }) => theme.spacing(8)};
  }
`;

const SectionLabel = styled.div`
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: ${({ theme }) => theme.spacing(4)};
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const Row = styled(Card).attrs({ as: Link })`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(3)};
  flex-wrap: wrap;
  text-decoration: none;
  color: inherit;
  cursor: pointer;

  &:hover {
    border-color: ${({ theme }) => theme.colors.borderStrong};
    box-shadow: ${({ theme }) => theme.shadow.lg};
  }
  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }
`;

const Main = styled.div`
  min-width: 0;
`;

const RowText = styled.div`
  font-size: 14px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const RowMeta = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
  margin-top: ${({ theme }) => theme.spacing(1)};
`;

const KindTag = styled.span`
  flex-shrink: 0;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  padding: ${({ theme }) => `${theme.spacing(0.5)} ${theme.spacing(2)}`};
  border-radius: ${({ theme }) => theme.radius.pill};
  color: ${({ theme }) => theme.colors.primary};
  background: ${({ theme }) => theme.colors.primarySoft};
`;

const EmptyHint = styled.p`
  margin: 0;
  font-size: 13px;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

/** Одна цитата-строка: пункт протокола с таймкодом → ведёт на совещание-источник. */
function CitationRow({ m }: { m: ArchiveMatch }) {
  return (
    <Row to={`/agents/meetings/meetings/${m.meeting_id}`}>
      <Main>
        <RowText>
          {m.timecode ? `[${m.timecode}] ` : ''}
          {m.content}
        </RowText>
        <RowMeta>
          {m.meeting_title}
          {m.scheduled_at ? ` · ${fmtMeetingDate(m.scheduled_at)}` : ''}
          {m.department_name ? ` · ${m.department_name}` : ''}
        </RowMeta>
      </Main>
      <KindTag>{protocolKindLabel(m.kind)}</KindTag>
    </Row>
  );
}

/**
 * Поиск по архиву утверждённых протоколов (этап 3.1). Гибрид лексика+вектор →
 * цитаты (совещание+пункт+таймкод). Кнопка «Ответ ИИ» синтезирует ответ СТРОГО со
 * ссылками (§3.1 — без цитаты нет ответа); ответственный/срок только из текста пункта.
 */
export function MeetingsArchive() {
  const { can_read } = useAgentPermission('meetings');
  const isAdmin = useMeetingDeleteAccess();
  const [query, setQuery] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const { data: deptsData } = useListMeetingDepartmentsQuery();
  const [runSearch, { data: searchData, isFetching: searching }] =
    useLazySearchMeetingsArchiveQuery();
  const [runAnswer, { data: answerData, isLoading: answering, reset: resetAnswer }] =
    useAnswerMeetingsArchiveMutation();
  const [reindex, { isLoading: reindexing }] = useReindexMeetingsArchiveMutation();
  const [reindexMsg, setReindexMsg] = useState('');

  const depts = deptsData?.departments ?? [];
  const matches = searchData?.matches ?? [];
  const trimmed = query.trim();

  function search() {
    if (!trimmed) return;
    resetAnswer();
    void runSearch({ query: trimmed, department_id: departmentId || undefined });
  }

  function ask() {
    if (!trimmed) return;
    void runAnswer({ query: trimmed, department_id: departmentId || undefined });
  }

  async function reindexArchive() {
    setReindexMsg('');
    try {
      const res = await reindex({ force: true }).unwrap();
      setReindexMsg(`Поставлено на переиндексацию: ${res.enqueued} протокол(ов).`);
    } catch {
      setReindexMsg('Не удалось: архив выключен (OSNOVA_MEETINGS_RAG_ENABLED=off) или нет прав.');
    }
  }

  if (!can_read) {
    return (
      <FallbackBanner tone="danger" title="Доступ запрещён">
        У вас нет прав на раздел «Совещания».
      </FallbackBanner>
    );
  }

  return (
    <div>
      <Intro>
        Поиск по всем утверждённым протоколам совещаний. Результат — точные цитаты пунктов со
        ссылкой на совещание, пункт и таймкод. «Ответ ИИ» формирует связный ответ строго по
        найденным пунктам — без домыслов; исполнитель и срок берутся только из текста протокола.
      </Intro>

      <Form
        onSubmit={(e) => {
          e.preventDefault();
          search();
        }}
      >
        <QueryInput
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Например: сроки по благоустройству двора"
          aria-label="Поисковый запрос"
        />
        <FilterSelect
          value={departmentId}
          onChange={(e) => setDepartmentId(e.target.value)}
          aria-label="Фильтр по отделу"
        >
          <option value="">— все отделы —</option>
          {depts.map((d) => (
            <option value={d.id} key={d.id}>
              {d.name}
            </option>
          ))}
        </FilterSelect>
        <Button type="submit" $variant="primary" disabled={!trimmed || searching}>
          {searching ? 'Ищу…' : 'Найти'}
        </Button>
        <Button type="button" $variant="secondary" onClick={ask} disabled={!trimmed || answering}>
          {answering ? 'Думаю…' : 'Ответ ИИ'}
        </Button>
        {isAdmin && (
          <Button
            type="button"
            $variant="secondary"
            onClick={() => void reindexArchive()}
            disabled={reindexing}
            title="Переиндексировать все утверждённые протоколы"
          >
            {reindexing ? 'Переиндексирую…' : 'Переиндексировать архив'}
          </Button>
        )}
      </Form>
      {reindexMsg && <CiteHint>{reindexMsg}</CiteHint>}

      {answerData && (
        <AnswerCard>
          <AnswerLabel>Ответ ИИ</AnswerLabel>
          <AnswerText>{answerData.answer}</AnswerText>
          {answerData.citations.length > 0 && (
            <>
              <CiteHint>Источники ({answerData.citations.length}):</CiteHint>
              <List style={{ marginTop: 8 }}>
                {answerData.citations.map((m) => (
                  <CitationRow m={m} key={m.chunk_id} />
                ))}
              </List>
            </>
          )}
        </AnswerCard>
      )}

      {searchData && (
        <Section>
          <SectionLabel>Найденные пункты протоколов</SectionLabel>
          {matches.length > 0 ? (
            <List>
              {matches.map((m) => (
                <CitationRow m={m} key={m.chunk_id} />
              ))}
            </List>
          ) : (
            <EmptyHint>
              {searching
                ? 'Ищу…'
                : 'Ничего не найдено. Уточните запрос или снимите фильтр по отделу. Архив наполняется утверждёнными протоколами.'}
            </EmptyHint>
          )}
        </Section>
      )}
    </div>
  );
}
